import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  Paper,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  Home as HomeIcon,
  Security as SecurityIcon,
  Lock as LockIcon,
  Schedule as ScheduleIcon,
  Shield as ShieldIcon,
  Visibility as VisibilityIcon,
  ContactSupport as ContactSupportIcon,
  Cookie as CookieIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  const [expandedFAQ, setExpandedFAQ] = useState(false);

  const handleFAQChange = (panel) => (event, isExpanded) => {
    setExpandedFAQ(isExpanded ? panel : false);
  };

  const faqData = [
    {
      id: 'faq1',
      question: 'Puis-je supprimer mon compte et toutes mes données ?',
      answer: 'Oui, vous pouvez supprimer votre compte à tout moment en nous contactant. Toutes vos données personnelles seront supprimées dans un délai de 30 jours, sauf obligations légales contraires.'
    },
    {
      id: 'faq2',
      question: 'Comment mes paiements sont-ils sécurisés ?',
      answer: 'Nous utilisons Stripe et PayPal, des leaders mondiaux en sécurité des paiements. Vos données bancaires ne transitent jamais par nos serveurs et sont chiffrées selon les standards PCI DSS.'
    },
    {
      id: 'faq3',
      question: 'Qui peut voir mon profil professionnel ?',
      answer: 'Seules les organisations inscrites sur Mission Sport peuvent consulter les profils des professionnels. Vos informations personnelles (email, téléphone) ne sont partagées qu\'après votre accord explicite.'
    },
    {
      id: 'faq4',
      question: 'Utilisez-vous mes données à des fins publicitaires ?',
      answer: 'Non, nous ne vendons jamais vos données à des tiers. Nous utilisons uniquement vos informations pour améliorer nos services et faciliter la mise en relation professionnelle.'
    }
  ];

  const privacySections = [
    {
      icon: <VisibilityIcon />,
      title: 'Collecte des données',
      content: 'Nous collectons uniquement les données nécessaires au fonctionnement de notre plateforme : informations de profil (nom, email, qualifications), données de navigation pour améliorer l\'expérience utilisateur, et informations de paiement sécurisées via nos partenaires.'
    },
    {
      icon: <SecurityIcon />,
      title: 'Utilisation des données',
      content: 'Vos données servent exclusivement à faciliter la mise en relation entre professionnels et organisations sportives, personnaliser votre expérience, assurer la sécurité des transactions, et vous informer des opportunités pertinentes.'
    },
    {
      icon: <ShieldIcon />,
      title: 'Partage des données',
      content: 'Nous partageons vos données uniquement avec nos partenaires de confiance (Stripe/PayPal pour les paiements, services d\'emailing) et jamais à des fins commerciales tierces. Votre consentement est toujours requis.'
    },
    {
      icon: <LockIcon />,
      title: 'Sécurité',
      content: 'Vos données sont protégées par un chiffrement de niveau bancaire, une authentification JWT sécurisée, et un hébergement sur des infrastructures certifiées (AWS/GCP) avec surveillance 24h/24.'
    },
    {
      icon: <ContactSupportIcon />,
      title: 'Vos droits',
      content: 'Conformément au RGPD, vous disposez des droits d\'accès, de rectification, de suppression, de portabilité et d\'opposition. Contactez-nous pour exercer ces droits en toute simplicité.'
    },
    {
      icon: <CookieIcon />,
      title: 'Cookies',
      content: 'Nous utilisons des cookies essentiels au fonctionnement du site et des cookies analytiques pour améliorer nos services. Vous pouvez gérer vos préférences via les paramètres de votre navigateur.'
    },
    {
      icon: <ScheduleIcon />,
      title: 'Durée de conservation',
      content: 'Vos données sont conservées pendant la durée de votre utilisation active de la plateforme, puis archivées 3 ans pour les obligations légales avant suppression définitive.'
    }
  ];

  return (
    <div className="privacy-page">
      <Helmet>
        <title>Politique de confidentialité | Mission Sport</title>
        <meta 
          name="description" 
          content="Découvrez comment Mission Sport protège vos données et respecte votre vie privée, conformément aux meilleures pratiques de sécurité et de transparence."
        />
        <meta name="keywords" content="politique confidentialité, protection données, RGPD, Mission Sport, vie privée, sécurité" />
        <meta property="og:title" content="Politique de confidentialité | Mission Sport" />
        <meta property="og:description" content="Découvrez comment Mission Sport protège vos données et respecte votre vie privée, conformément aux meilleures pratiques de sécurité et de transparence." />
        <meta property="og:url" content="https://missionsport.fr/privacy" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Politique de confidentialité | Mission Sport" />
        <meta name="twitter:description" content="Découvrez comment Mission Sport protège vos données et respecte votre vie privée." />
      </Helmet>

      <Container maxWidth="lg" className="privacy-container">
        {/* Breadcrumb */}
        <Box className="breadcrumb-section">
          <Breadcrumbs aria-label="breadcrumb">
            <Link 
              component={RouterLink} 
              to="/" 
              color="inherit" 
              className="breadcrumb-link"
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Accueil
            </Link>
            <Typography color="text.primary">Politique de confidentialité</Typography>
          </Breadcrumbs>
        </Box>

        {/* Header */}
        <header className="privacy-header">
          <Box className="header-content">
            <ShieldIcon className="header-icon" />
            <Typography variant="h1" className="privacy-title">
              Politique de confidentialité
            </Typography>
            <Typography variant="h2" className="privacy-subtitle">
              Votre confiance est notre priorité
            </Typography>
            <Typography variant="body1" className="privacy-intro">
              Chez Mission Sport, nous nous engageons à protéger votre vie privée et à traiter vos données 
              personnelles avec le plus grand respect. Cette politique explique de manière transparente comment 
              nous collectons, utilisons et protégeons vos informations.
            </Typography>
            <Typography variant="body2" className="last-update">
              Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}
            </Typography>
          </Box>
        </header>

        <main className="privacy-main">
          {/* Introduction */}
          <Paper className="intro-section" elevation={1}>
            <Typography variant="h3" className="section-title">
              Pourquoi cette politique ?
            </Typography>
            <Typography variant="body1" className="section-text">
              La protection de vos données personnelles est un droit fondamental. En tant que plateforme de 
              mise en relation dans le secteur sportif, nous traitons des informations sensibles qui méritent 
              une protection exemplaire. Cette politique détaille nos engagements et vos droits.
            </Typography>
            <Typography variant="body1" className="section-text">
              Nous respectons scrupuleusement le Règlement Général sur la Protection des Données (RGPD) et 
              appliquons les meilleures pratiques internationales en matière de cybersécurité.
            </Typography>
          </Paper>

          {/* Sections principales */}
          <Box className="privacy-sections">
            <Typography variant="h3" className="sections-title">
              Comment nous protégeons vos données
            </Typography>
            <Grid container spacing={3}>
              {privacySections.map((section, index) => (
                <Grid item xs={12} md={6} lg={4} key={index}>
                  <Card className="privacy-card" elevation={2}>
                    <CardContent>
                      <Box className="card-header">
                        <Box className="card-icon">
                          {section.icon}
                        </Box>
                        <Typography variant="h4" className="card-title">
                          {section.title}
                        </Typography>
                      </Box>
                      <Typography variant="body2" className="card-content">
                        {section.content}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Divider className="main-divider" />

          {/* Contact */}
          <Paper className="contact-section" elevation={1}>
            <Box className="contact-header">
              <ContactSupportIcon className="contact-icon" />
              <Typography variant="h3" className="section-title">
                Une question sur vos données ?
              </Typography>
            </Box>
            <Typography variant="body1" className="section-text">
              Notre équipe est à votre disposition pour répondre à toutes vos questions concernant 
              la protection de vos données personnelles.
            </Typography>
            <Box className="contact-info">
              <Typography variant="body1">
                <strong>Email :</strong> <a href="mailto:privacy@missionsport.fr" className="contact-link">privacy@missionsport.fr</a>
              </Typography>
              <Typography variant="body1">
                <strong>Formulaire :</strong> <Link component={RouterLink} to="/contact" className="contact-link">Page de contact</Link>
              </Typography>
              <Typography variant="body1">
                <strong>Délégué à la protection des données :</strong> dpo@missionsport.fr
              </Typography>
            </Box>
          </Paper>

          {/* FAQ */}
          <Box className="faq-section">
            <Typography variant="h3" className="section-title">
              Questions fréquentes
            </Typography>
            <Typography variant="body1" className="section-subtitle">
              Les réponses aux questions les plus courantes sur la protection de vos données.
            </Typography>
            
            {faqData.map((faq) => (
              <Accordion 
                key={faq.id}
                expanded={expandedFAQ === faq.id} 
                onChange={handleFAQChange(faq.id)}
                className="faq-accordion"
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`${faq.id}-content`}
                  id={`${faq.id}-header`}
                  className="faq-summary"
                >
                  <Typography variant="h6" className="faq-question">
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails className="faq-details">
                  <Typography variant="body1" className="faq-answer">
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>

          {/* Engagement final */}
          <Paper className="commitment-section" elevation={1}>
            <Box className="commitment-content">
              <ShieldIcon className="commitment-icon" />
              <Typography variant="h3" className="commitment-title">
                Notre engagement
              </Typography>
              <Typography variant="body1" className="commitment-text">
                Mission Sport s'engage à maintenir les plus hauts standards de protection des données. 
                Nous investissons continuellement dans la sécurité, la transparence et le respect de votre vie privée.
              </Typography>
              <Typography variant="body1" className="commitment-text">
                Votre confiance est la base de notre relation. Nous la méritons chaque jour par nos actions 
                et notre transparence.
              </Typography>
            </Box>
          </Paper>
        </main>
      </Container>
    </div>
  );
};

export default PrivacyPolicy;